import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'

import { MessagePopup, Popup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { FormikProps } from 'formik'
import { Form, Formik } from 'formik'
import CogIcon from 'mdi-react/CogIcon'
import HandshakeOutlineIcon from 'mdi-react/HandshakeOutlineIcon'
import PencilIcon from 'mdi-react/PencilIcon'
import RecycleIcon from 'mdi-react/RecycleIcon'
import { useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { UnsavedChangesButton } from '@/components/UnsavedChangesButton'
import { ADMIN_ROLES, useIsAdmin, useIsThreeStepIT } from '@/hooks/access'
import useUser from '@/hooks/user'
import type { VerifyCategoriesByLeasingVendor } from '@/modules/ecom-policy/hooks'
import { useCorrectCategoryClassifications } from '@/modules/ecom-policy/hooks'
import { usePolicyEditAccess, usePolicyId } from '@/modules/ecom-policy/policy/hooks'
import { usePolicyFilters } from '@/modules/ecom-policy/policy/Policy.context'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import type { PolicyExtended } from '@/modules/ecom-policy/types'
import { APP_ROUTES } from '@/util/app-routes'
import { validateEmail } from '@/util/formatters/text'

import { AdvancedSettings } from './AdvancedSettings'
import Amounts from './Amounts'
import BulletPoints from './BulletPoints'
import { Flags } from './Flags'
import { GreenpoolSettings } from './GreenpoolSettings'
import { getInitialValues, getPayload, setClassificationList } from './helpers'
import { useChangePolicySettings } from './hooks'
import Intervals from './Intervals'
import { LeaseSettings } from './LeaseSettings'
import { LeasingCompatibilityWarning } from './LeasingCompatibilityWarning'
import Name from './Name'
import type { FormFields } from './types'

import './Settings.styles.scss'

const Settings: FC<{ policy: PolicyExtended }> = ({ policy }) => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const isThreeStepIT = useIsThreeStepIT()
  const hasEditAccess = usePolicyEditAccess()
  const queryClient = useQueryClient()

  const role = useUser()?.role
  const policyId = usePolicyId()
  const [, setIsVatIncluded] = usePolicyFilters().isVatIncluded
  const isAdmin = useIsAdmin()
  const lockFields = policy.isLeasing ? policy.lockFields : []

  const formRef = useRef<FormikProps<FormFields>>(null)
  const [isPolicyNameModalOpen, setIsPolicyNameModalOpen] = useState(false)
  const [isAdvancedSettingsModalOpen, setIsAdvancedSettingsModalOpen] = useState(false)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const [isGreenpoolSettingsModalOpen, setIsGreenpoolSettingsModalOpen] = useState(false)
  const [isLeaseSettingsModalOpen, setIsLeaseSettingsModalOpen] = useState(false)

  const [isLeasingCompatibilityModalOpen, setIsLeasingCompatibilityModalOpen] =
    useState<boolean>(false)
  const [updatedCategories, setUpdatedCategories] = useState<string[]>([])
  const [orderIntervalIssue, setOrderIntervalIssue] = useState<number[] | undefined>(undefined)

  const {
    isLoadingPolicyOrCustomerVendors,
    leasingVendorCategories,
    removedCategories,
    leasingOrderIntervals,
    leasingVendorProductCategories,
    verifyLeasingCompatibility,
    verifyCategoriesByLeasingVendor,
    verifyLeasingOrderIntervals,
  } = useCorrectCategoryClassifications(policyId)

  const {
    isSaving,
    changePolicySettings,
    isErrorOnLeasingVendorUpdate,
    isLoadingOnLeasingVendorUpdate,
  } = useChangePolicySettings({
    onError: () => setIsErrorModalOpen(true),
  })

  const updatePolicyCategoryClassification = useMutation(
    ecomPolicyResources.updatePolicyCategoryClassification,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queries.policyDetails(policyId))
        queryClient.invalidateQueries(queries.policyProducts(policyId), { refetchType: 'all' })
      },
    }
  )

  const runLeasingCompatibilityCheck = () => {
    const { categoriesResult, intervalResult } = verifyLeasingCompatibility()

    setIsLeasingCompatibilityModalOpen(categoriesResult.showModal || intervalResult.showModal)
    setOrderIntervalIssue(
      intervalResult.showModal ? intervalResult.leasingOrderIntervals : undefined
    )

    if (
      !categoriesResult.showModal &&
      categoriesResult.leasingVendorCategories.length > 0 &&
      categoriesResult.removedPolicyCategories.length === 0
    ) {
      setUpdatedCategories([...categoriesResult.leasingVendorCategories])
    }
  }

  useEffect(() => {
    if (role && !ADMIN_ROLES.includes(role) && policy.isAdminRestricted) {
      navigate(APP_ROUTES.HOME)
    }
  }, [navigate, policy.isAdminRestricted, role])

  useEffect(() => {
    if (
      !isLoadingPolicyOrCustomerVendors &&
      !isLeasingCompatibilityModalOpen &&
      policy.isLeasing &&
      policy.leasingVendorId &&
      leasingVendorCategories.length === 0
    ) {
      runLeasingCompatibilityCheck()
    }
  }, [isLoadingPolicyOrCustomerVendors])

  useEffect(() => {
    setIsVatIncluded(policy.hasVat)
  }, [policy.hasVat, setIsVatIncluded])

  const formValidation = (values: FormFields) => {
    const errors: Partial<Record<keyof FormFields, string>> = {}

    if (values.greenpoolTypeId && !values.greenpoolNotificationEmail) {
      errors.greenpoolNotificationEmail = translate('This field is mandatory')
    }
    if (values.greenpoolNotificationEmail && !validateEmail(values.greenpoolNotificationEmail)) {
      errors.greenpoolNotificationEmail = translate('Invalid email format')
    }
    if (values.greenpoolTypeId === 'OPTIONAL' && !values.greenpoolSplashText) {
      errors.greenpoolSplashText = translate('This field is mandatory')
    }

    if (values.greenpoolTypeId && !values.greenpoolConfirmationText) {
      errors.greenpoolConfirmationText = translate('This field is mandatory')
    }

    if (!values.classificationList || values.classificationList.length === 0) {
      errors.classificationList = translate('At least one value must be selected')
    }

    if (values.isLeasing && !values.leasingVendorId) {
      errors.leasingVendorId = translate('This field is mandatory')
    }

    if (
      values.isLeasing &&
      values.classificationList &&
      values.classificationList.length > 0 &&
      leasingVendorCategories.length > 0
    ) {
      const isValid = values.classificationList.every(category =>
        leasingVendorCategories.includes(category)
      )
      if (!isValid) {
        errors.classificationList = translate(
          'Some selected policy types are not supported by the chosen leasing vendor. Please update your selection.'
        )
      }
    }

    return errors
  }

  const onFormSubmit = (formValues: FormFields) => {
    if (!hasEditAccess || !formRef.current?.dirty) return

    updatePolicyCategoryClassification.mutate({
      policyId: policy.id,
      payload: {
        categoryClassificationList: formValues.classificationList,
        isLeasing: formValues.isLeasing,
        leasingVendorId: formValues.leasingVendorId ?? null,
      },
    })

    changePolicySettings({
      policyId,
      payload: getPayload(formValues, isAdmin),
      leasingVendorId: formValues.leasingVendorId,
      isLeasing: formValues.isLeasing,
    })
  }

  return (
    <span className="policy-settings">
      <button className="edit-name-and-desc" onClick={() => setIsPolicyNameModalOpen(true)}>
        <h2>
          {policy.name} <PencilIcon />
        </h2>

        <h3>{policy.description}</h3>
      </button>

      <BulletPoints />

      <Formik<FormFields>
        enableReinitialize
        innerRef={formRef}
        initialValues={getInitialValues(policy)}
        initialTouched={{
          classificationList: true,
          orderInterval: true,
        }}
        validate={formValidation}
        onSubmit={onFormSubmit}
      >
        {({ errors, dirty, isValid, setFieldTouched, setFieldValue, handleSubmit, values }) => (
          <Form>
            <div className="input-cards">
              <Amounts isDisabled={!hasEditAccess} />
              <Intervals
                hideOrder={lockFields?.includes('order_interval')}
                hideCover={lockFields?.includes('cover_interval')}
                isDisabled={!hasEditAccess}
                leasingOrderIntervals={policy.isLeasing ? leasingOrderIntervals : undefined}
              />

              <Flags
                isActive={policy.isActive}
                customerId={policy.customerId}
                hideBuyout={lockFields?.includes('is_buyout_allowed')}
                isErrorOnLeasingVendorUpdate={isErrorOnLeasingVendorUpdate}
                isDisabled={!hasEditAccess}
                verifyLeasingVendorCategories={(acc: VerifyCategoriesByLeasingVendor) => {
                  const verify = verifyCategoriesByLeasingVendor(acc)
                  const intervalVerify = verifyLeasingOrderIntervals(acc)

                  setIsLeasingCompatibilityModalOpen(verify.showModal || intervalVerify.showModal)
                  setOrderIntervalIssue(
                    intervalVerify.showModal ? intervalVerify.leasingOrderIntervals : undefined
                  )

                  if (!verify.showModal) {
                    setUpdatedCategories([...verify.leasingVendorCategories])
                    const classificationList = setClassificationList(policy.classificationList)
                    setFieldValue('classificationList', classificationList)
                  }
                }}
              />
            </div>

            <Popup
              isOpen={isLeasingCompatibilityModalOpen}
              ariaLabel={translate('Policy type and order interval changed')}
            >
              <LeasingCompatibilityWarning
                isReadOnlyView={!hasEditAccess}
                leasingVendorCategories={leasingVendorCategories}
                removedCategories={removedCategories}
                leasingOrderIntervals={orderIntervalIssue}
                onClose={() => setIsLeasingCompatibilityModalOpen(false)}
                onCategoriesChange={(c: string[]) => {
                  setUpdatedCategories(c)
                }}
                onOrderIntervalChange={() => {
                  setOrderIntervalIssue(undefined)
                }}
              />
            </Popup>

            <Popup
              variant="wide"
              isOpen={isPolicyNameModalOpen}
              ariaLabel={translate('Policy name')}
              onClose={() => {
                /* the pop does not submit the values selected so before closing it is being checked that everything is fine
                 * and if not can not be closed without resolving the problems
                 */
                if (errors.classificationList) return
                setIsPolicyNameModalOpen(false)
              }}
            >
              <Name
                isLeasing={policy.isLeasing}
                isDisabled={!hasEditAccess}
                isPolicyTypeDisabled={
                  values.isLeasing &&
                  !!values.leasingVendorId &&
                  leasingVendorCategories.length === 0
                }
                updatedCategories={updatedCategories}
                leasingVendorProductCategories={leasingVendorProductCategories}
              />
            </Popup>

            <Popup
              ariaLabel={translate('Advanced policy settings')}
              isOpen={isAdvancedSettingsModalOpen}
              onClose={() => setIsAdvancedSettingsModalOpen(false)}
            >
              <AdvancedSettings policy={policy} isDisabled={!hasEditAccess} />
            </Popup>

            <Popup
              ariaLabel={translate('Greenpool settings')}
              isOpen={isGreenpoolSettingsModalOpen}
              onClose={() => {
                if (
                  errors.greenpoolNotificationEmail ||
                  errors.greenpoolSplashText ||
                  errors.greenpoolConfirmationText
                ) {
                  setFieldTouched('greenpoolNotificationEmail', true)
                  setFieldTouched('greenpoolSplashText', true)
                  setFieldTouched('greenpoolConfirmationText', true)
                  return
                }
                setIsGreenpoolSettingsModalOpen(false)
              }}
            >
              <GreenpoolSettings isDisabled={!hasEditAccess} />
            </Popup>

            <Popup
              ariaLabel={translate('Lease settings')}
              isOpen={isLeaseSettingsModalOpen}
              onClose={() => setIsLeaseSettingsModalOpen(false)}
            >
              <LeaseSettings
                policyId={policy.id}
                isDisabled={!hasEditAccess}
                onClose={() => setIsLeaseSettingsModalOpen(false)}
              />
            </Popup>

            <footer>
              <Button
                className={twJoin(
                  (errors.greenpoolConfirmationText ||
                    errors.greenpoolSplashText ||
                    errors.greenpoolNotificationEmail) &&
                    'border-red bg-red border-2 border-solid bg-opacity-10'
                )}
                Icon={<RecycleIcon className={twJoin(!isThreeStepIT && 'fill-green-500')} />}
                onClick={() => setIsGreenpoolSettingsModalOpen(true)}
              >
                <Translation id="Greenpool settings" />
              </Button>

              {isAdmin && (
                <Button Icon={<CogIcon />} onClick={() => setIsAdvancedSettingsModalOpen(true)}>
                  <Translation id="Advanced settings" />
                </Button>
              )}

              {policy.isLeasing && policy.leasingVendorId && (
                <Button
                  Icon={<HandshakeOutlineIcon />}
                  onClick={() => setIsLeaseSettingsModalOpen(true)}
                  isLoading={isLoadingOnLeasingVendorUpdate}
                >
                  <Translation id="Lease settings" />
                </Button>
              )}

              <UnsavedChangesButton
                hasUnsavedChanges={dirty}
                disabled={
                  !hasEditAccess ||
                  isSaving ||
                  updatePolicyCategoryClassification.isLoading ||
                  isLoadingOnLeasingVendorUpdate
                }
                error={!isValid}
                isLoading={
                  isSaving ||
                  updatePolicyCategoryClassification.isLoading ||
                  isLoadingOnLeasingVendorUpdate
                }
                onClick={handleSubmit}
              />
            </footer>
          </Form>
        )}
      </Formik>

      <MessagePopup
        title={<Translation id="Saving settings error" />}
        message={<Translation id="Ooops! Something went wrong. Please contact our support." />}
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
      />
    </span>
  )
}

export default Settings
